/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import {
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@material-ui/core";
import { Label, OptionGroup } from "components/Styled";
import {
  ContentWrapper,
  ErrorMessage,
  ConfirmButton,
  SectionTitle,
} from "./Styled";
import ActionButton from "./ActionButton";
import AdvancedOptions from "./AdvancedOptions";
import {
  nameFormatDefaults,
  getContentErrors,
  containsEmailType,
  isEmailType,
} from "./utils";
import { useRuleContext } from "../../context/ruleContext";

import { Content, Storage } from "@dashboard-v3/api";
import { RuleType } from "pages/Rules/common/types";
import { ActionControl, ActionState } from "./types";
import { getActionState } from "pages/Rules/Form/context/ruleSelectors";

export default function RuleActions() {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const action = getActionState(state.rule);
  const types = action.content?.map(c => c.type);
  const [controls, setControls] = useState<ActionControl>({
    isError: false,
    errorKey: "",
  });

  useEffect(() => {
    const errors = getContentErrors(types);
    const optText = !types?.includes("ATTACHMENTS")
      ? t("attachments")
      : t("emailBodyInPdf");
    setControls(prev =>
      produce(prev, draft => {
        if (errors.get("EMPTY")) {
          draft.isError = true;
          draft.errorKey = t("selectOneOption");
        } else if (errors.get(state.type)) {
          draft.isError = true;
          draft.errorKey = `${optText} ${t("optionMustBeSelected")}`;
        } else {
          draft.isError = false;
          draft.errorKey = "";
        }
      })
    );
  }, [types?.length]);

  function updateLocationFields(content: Content[]) {
    const location: Partial<Storage["location"]> = {};
    const contentTypes = content.map(c => c.type);

    if (!contentTypes.some(v => isEmailType(v))) {
      location.emailSubfolder = null;
    }

    if (!contentTypes.includes("ATTACHMENTS")) {
      location.attachmentsSubfolder = null;
    }

    if (!contentTypes.includes("PDF")) {
      location.bodySubfolder = null;
    }

    dispatch({
      type: "updateStep",
      payload: { step: "filePath", changes: { location } },
    });
  }

  function updateValues(partialChange: Partial<ActionState>) {
    const updated = { ...action, ...partialChange };
    const isFilepathStep = "storageFilepath" in state.steps;
    const isPasswordSetting = "passwordNotificationText" in updated;

    if (isFilepathStep && !isPasswordSetting) {
      updateLocationFields(updated.content);
    }

    dispatch({
      type: "updateStep",
      payload: { step: "action", changes: updated },
    });
  }

  const updateContentType = (val: Content["type"], state: ActionState) => {
    return produce(state, draft => {
      if (val === "EML" && types.includes("MSG")) {
        draft.content = state.content.filter(c => c.type !== "MSG");
      } else if (val === "EML" && types.includes("ORIGINAL")) {
        draft.content = state.content.filter(c => c.type !== "ORIGINAL");
      } else if (types.includes(val)) {
        draft.content = state.content.filter(c => c.type !== val);
      } else {
        draft.content.push({
          type: val,
          nameFormat: nameFormatDefaults(val),
        });
      }
    });
  };

  const handleContentType = (val: Content["type"]) => _ => {
    const updated = updateContentType(val, action);
    updateValues(updated);
  };

  const handleSaveFormat = event => {
    const { value }: { value: Content["type"] } = event.target;
    const changes = action.content.map(item => {
      if (isEmailType(item.type)) {
        return { ...item, type: value };
      }
      return item;
    });

    updateValues({ content: changes });
  };

  const handleConfirm = () => {
    dispatch({ type: "confirmStep", payload: "action" });
  };

  const isConfirmDisabled = () => {
    const { action } = state.steps;
    return !action.valid || action.done;
  };

  const isDisableOptions = () => {
    return (
      controls.isError ||
      state.type === "COPY_EMAILS" ||
      state.type === "DRAG_AND_DROP"
    );
  };

  if (state.type === "BLOCK_ATTACHMENTS") {
    return (
      <Paper>
        <SectionTitle type={state.type} />
        <ContentWrapper>
          <OptionGroup>
            <Label wording={t("allYourAttachmentsWillBeBlocked")} />
          </OptionGroup>
          <ConfirmButton
            fullWidth
            size="large"
            wording={t("confirm")}
            onClick={handleConfirm}
            disabled={isConfirmDisabled()}
          />
        </ContentWrapper>
        <AdvancedOptions
          ruleType={state.type}
          targetType={state.rule?.targetType}
          values={action}
          setValues={updateValues}
          disabled={false}
        />
      </Paper>
    );
  }

  return (
    <Paper>
      <SectionTitle type={state.type} />
      <ContentWrapper>
        <OptionGroup>
          <Label gutterBottom wording={t("selectWhatToSave")} />
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <ActionButton
                wording={t("email")}
                onClick={handleContentType("EML")}
                selected={containsEmailType(action.content)}
                testid="rules-copy_email-btn"
                disabled={isEmailDisabled(state.type)}
              />
            </Grid>
            <Grid item xs={4}>
              <ActionButton
                wording={t("attachments")}
                onClick={handleContentType("ATTACHMENTS")}
                selected={types.includes("ATTACHMENTS")}
                testid="rules-copy_attachments-btn"
              />
            </Grid>
            <Grid item xs={4}>
              <ActionButton
                wording={t("emailBodyInPdf")}
                onClick={handleContentType("PDF")}
                selected={types.includes("PDF")}
                testid="rules-copy_pdf-btn"
                disabled={isPDFDisabled(state.type)}
              />
            </Grid>
          </Grid>
          <ErrorMessage
            isError={controls.isError}
            wording={controls.errorKey}
          />
        </OptionGroup>
        {containsEmailType(action.content) && (
          <OptionGroup>
            <Label wording={t("emailSaveFormat")} />
            <RadioGroup
              value={types.find(t => isEmailType(t))}
              onChange={handleSaveFormat}
              row
            >
              <FormControlLabel
                label={t("ruleAction.saveFormat.eml")}
                value="EML"
                control={<Radio color="primary" />}
              />
              <FormControlLabel
                label={t("ruleAction.saveFormat.msg")}
                value="MSG"
                control={<Radio color="primary" />}
              />
              {state.rule.actionType === "DRAG_AND_DROP" && (
                <FormControlLabel
                  label={t("ruleAction.saveFormat.original")}
                  value="ORIGINAL"
                  control={<Radio color="primary" />}
                />
              )}
            </RadioGroup>
          </OptionGroup>
        )}
        <ConfirmButton
          fullWidth
          size="large"
          wording={t("confirm")}
          onClick={handleConfirm}
          disabled={isConfirmDisabled()}
        />
      </ContentWrapper>
      <AdvancedOptions
        ruleType={state.type}
        targetType={state.rule?.targetType}
        values={action}
        setValues={updateValues}
        disabled={isDisableOptions()}
      />
    </Paper>
  );
}

function isEmailDisabled(type: RuleType) {
  if (type === "ZIP_ATTACHMENTS") return true;

  return false;
}

function isPDFDisabled(type: RuleType) {
  if (type === "ZIP_ATTACHMENTS") return true;

  return false;
}
